import appLogger from "../../infra/logger/appLogger";
import { FetchStatus } from "../user/userSettings/types";
import fetchDiscoveryRecommendations, {} from "./fetchDiscoveryRecommendations";
const discoveryRecommendationRowLogger = appLogger.withTag("DiscoveryRecommendationRow");
export const RECOMMENDATION_USER_WAIT_TIMEOUT_MS = 10_000;
/**
 * The identity a recommendation request is made under, or an empty string
 * while it is not yet known — which parks the row rather than requesting.
 *
 * Nothing is returned until auth has settled: a persisted guest id is in the
 * store from the first render, so reading it earlier would request guest
 * recommendations for a viewer who turns out to be signed in, and cache them
 * under the entry Home shares. A signed-in session also keeps that guest id
 * until the profile arrives, so the guest id stays withheld while
 * `isAuthenticated` is set and `onlyUserId` is still empty. Both apps' Home
 * rows and `useDiscoverFavorite` gate on the same flag.
 *
 * Guest ids are used unprefixed, matching what both apps' Home rows already
 * send, so this row and Home resolve to the same Recombee user and the same
 * cache entry. The `gid_` prefix some surfaces use is for interaction events
 * only.
 */
export function readDiscoveryRecommendationUserId({ isAuthLoaded, isAuthenticated, onlyUserId, guestUserId, }) {
    if (!isAuthLoaded) {
        return "";
    }
    if (isAuthenticated && !onlyUserId) {
        return "";
    }
    return onlyUserId ?? guestUserId ?? "";
}
/**
 * Authenticated viewers must not request until their settings fetch has
 * settled. `blockedUsers` and tag filters are empty until then, and a write
 * in that window is stored under the cache entry Home shares.
 *
 * Guests never load those settings, so an empty filter set is their real
 * input. A failed fetch must not park the row until the scheduler gives up.
 */
export function areDiscoveryRecommendationFiltersReady({ isAuthenticated, isUserSettingsLoaded, settingsFetchStatus, }) {
    if (!isAuthenticated) {
        return true;
    }
    if (isUserSettingsLoaded) {
        return true;
    }
    return settingsFetchStatus === FetchStatus.failed;
}
/**
 * Resolves once the viewer's id is known. The wait is bounded because the row
 * holds one of the scheduler's four request slots while it waits, so an id
 * that never arrives must not cost the page that slot for the whole visit.
 */
function waitForDiscoveryRecommendationUser({ signal, readUserId, listeners, }) {
    const currentUserId = readUserId();
    if (currentUserId.length > 0) {
        return Promise.resolve({ status: "ready", userId: currentUserId });
    }
    // An already-aborted signal never emits "abort", so waiting on it would only
    // ever end on the timeout.
    if (signal.aborted) {
        return Promise.resolve({ status: "aborted" });
    }
    return new Promise((resolve) => {
        const waitTimer = {};
        const waitState = {
            onAbort: () => {
                waitState.finish({ status: "aborted" });
            },
            onContextChange: () => {
                const userId = readUserId();
                if (userId.length > 0) {
                    waitState.finish({ status: "ready", userId });
                }
            },
            finish: (result) => {
                clearTimeout(waitTimer.id);
                listeners.delete(waitState.onContextChange);
                signal.removeEventListener("abort", waitState.onAbort);
                resolve(result);
            },
        };
        listeners.add(waitState.onContextChange);
        signal.addEventListener("abort", waitState.onAbort);
        waitTimer.id = setTimeout(() => {
            waitState.finish({ status: "timeout" });
        }, RECOMMENDATION_USER_WAIT_TIMEOUT_MS);
    });
}
const emptyRecommendationResult = {
    outcome: { kind: "empty" },
    didConsumeAutoRetry: false,
};
/**
 * Loads one Discovery recommendation row through the surface's injected loader.
 * The scheduler is the one-request-per-visit guard. An empty or failed load is
 * an empty row, so this never throws and never asks for a retry.
 */
export default async function loadDiscoveryRecommendationRow({ signal, readRecommendation, listeners, }) {
    const userWait = await waitForDiscoveryRecommendationUser({
        signal,
        readUserId: () => readRecommendation().userId,
        listeners,
    });
    if (userWait.status === "aborted" || signal.aborted) {
        return { outcome: { kind: "cancelled" }, didConsumeAutoRetry: false };
    }
    if (userWait.status === "timeout") {
        discoveryRecommendationRowLogger.warn("Gave up waiting for the viewer id; dropping the recommendation row", {
            timeoutMs: RECOMMENDATION_USER_WAIT_TIMEOUT_MS,
        });
        return emptyRecommendationResult;
    }
    const outcome = await fetchDiscoveryRecommendations({
        signal,
        userId: userWait.userId,
        loadRecommendations: readRecommendation().loadRecommendations,
    });
    return { outcome, didConsumeAutoRetry: false };
}
